import { type DriveFile } from '@/shared/DriveTypes';
import { log } from '~/utils/logger';
import { sort } from '~/utils/sort';
import { omit, omitUndef } from '~/utils/utils';

const getFileIndex = (file: DriveFile, sortedChildren: DriveFile[]) => {
  return sortedChildren.findIndex((o) => o.id === file.id);
};

const getNextIndex = (currentIndex: number, direction: Direction, length: number) => {
  if (direction === 'bottom') return Infinity;
  if (direction === 'top') return 0;
  if (direction === 'up') return currentIndex - 1;
  if (currentIndex >= length - 2) return Infinity;
  return currentIndex + 1;
};

const removeFileIndex = (file: DriveFile): DriveFile => {
  if (!file.properties?.index) return file;
  const properties = omitUndef(file.properties, 'index');
  if (!properties) return omit(file, 'properties') as DriveFile;
  return { ...file, properties };
};

const getNextSibling = ({
  siblingFile,
  currentFileIndex,
  currentSiblingIndex,
  direction,
  sortedChildren,
  nextFileIndex,
  currentFileIndexValue,
}: {
  siblingFile: DriveFile;
  currentFileIndex: number;
  currentSiblingIndex: number;
  direction: Direction;
  sortedChildren: DriveFile[];
  nextFileIndex: number;
  currentFileIndexValue: number;
}): DriveFile => {
  const siblingHasIndex = !!siblingFile.properties?.index;

  const defaultSiblingIndex = siblingHasIndex
    ? sort(sortedChildren.map((o) => (o.id === siblingFile.id ? removeFileIndex(o) : o))).findIndex(
        (o) => o.id === siblingFile.id
      )
    : sortedChildren.findIndex((o) => o.id === siblingFile.id);

  const isDefaultPosition = currentSiblingIndex === defaultSiblingIndex;

  const getSiblingNextIndex = () => {
    if (direction === 'top') return siblingHasIndex ? 1 : undefined;
    if (direction === 'down') {
      if (nextFileIndex === Infinity && siblingHasIndex && Number(siblingFile.properties?.index) === Infinity) {
        return sortedChildren.length - 2;
      }
      if (isDefaultPosition) return undefined;
      return currentSiblingIndex - 1;
    }
    if (direction === 'bottom') {
      if (defaultSiblingIndex === sortedChildren.length - 1) return undefined;
      const priorSiblingIndex = currentSiblingIndex - 1;
      if (priorSiblingIndex < 0) return sortedChildren.length - 2;
      const priorSibling = sortedChildren[priorSiblingIndex];
      if (priorSibling?.properties?.index) {
        const priorIndex = Number(priorSibling.properties.index);
        return priorIndex === Infinity ? sortedChildren.length - 2 : priorIndex + 1;
      }
      return sortedChildren.length - 2;
    }
    if (siblingHasIndex) return currentFileIndexValue;
    if (isDefaultPosition) return undefined;
    return currentFileIndex;
  };

  const nextSiblingIndex = getSiblingNextIndex();

  if (nextSiblingIndex !== undefined) {
    return {
      ...siblingFile,
      properties: {
        ...siblingFile.properties,
        index: `${nextSiblingIndex}`,
      },
    };
  }
  return removeFileIndex(siblingFile);
};

const getMoveValue = (direction: Direction, currentIndex: number, length: number) => {
  if (direction === 'up') return -1;
  if (direction === 'down') return 1;
  if (direction === 'top') return -currentIndex;
  return length - 1; // bottom
};

export type Direction = 'up' | 'down' | 'top' | 'bottom';
const getMovedFiles = ({
  files,
  file,
  direction,
}: {
  files: DriveFile[];
  file: DriveFile;
  direction: Direction;
}): {
  nextFile: DriveFile;
  nextSiblingFile: DriveFile | undefined;
  nextFiles: DriveFile[];
} => {
  const children = files.filter((o) => o.parents?.[0] === file.parents?.[0]);
  const sortedChildren = sort(children);
  const currentFileIndex = getFileIndex(file, sortedChildren);

  if (currentFileIndex === 0 && direction === 'up') {
    return {
      nextFiles: files,
      nextFile: file,
      nextSiblingFile: undefined,
    };
  }

  const moveValue = getMoveValue(direction, currentFileIndex, children.length);
  const currentSiblingIndex = Math.min(currentFileIndex + moveValue, children.length - 1);
  const siblingFile = sortedChildren[currentSiblingIndex];

  if (!siblingFile) throw new Error('siblingFile is undefined');
  const siblingIndexValue = siblingFile.properties?.index
    ? Number(siblingFile.properties.index) === Infinity
      ? Infinity
      : Number(siblingFile.properties.index)
    : currentSiblingIndex;

  const nextIndex = file.properties?.index
    ? siblingIndexValue
    : getNextIndex(currentFileIndex, direction, children.length);

  const nextFile = {
    ...file,
    properties: {
      ...file.properties,
      index: `${nextIndex}`,
    },
  };
  const currentFileIndexValue = file.properties?.index
    ? Number(file.properties.index) === Infinity
      ? Infinity
      : Number(file.properties.index)
    : currentFileIndex;

  const nextSiblingFile = getNextSibling({
    siblingFile,
    currentFileIndex,
    currentSiblingIndex,
    direction,
    sortedChildren,
    nextFileIndex: nextIndex,
    currentFileIndexValue,
  });

  const nextFiles = files.map((o) => {
    if (o.id === file.id) return nextFile;
    if (o.id === nextSiblingFile.id) return nextSiblingFile;
    return o;
  });

  log.dev('getMovedFiles:', {
    fileName: file.name,
    fileId: file.id,
    currentFileIndex,
    'file.properties?.index': file.properties?.index,
    siblingFileName: siblingFile.name,
    siblingId: siblingFile.id,
    currentSiblingIndex,
    'siblingFile.properties?.index': siblingFile.properties?.index,
    nextIndex,
    direction,
  });

  return {
    nextFiles,
    nextFile,
    nextSiblingFile,
  };
};
export default getMovedFiles;
